<template>
  <div>
    <div v-if="!newsStore.newsItem" class="py-10 flex justify-center">
      <BaseSpinner />
    </div>
    <div v-else class="flex flex-col gap-6">
      <article
        class="max-w-full mx-auto space-y-4 p-4 sm:p-8 bg-white rounded-xl text-gray-900"
      >
        <div class="w-full flex justify-between gap-4 mb-8">
          <div class="flex items-center gap-4 flex-wrap">
            <div
              v-if="categories.length || newsStore.newsItem.premium"
              class="flex flex-wrap items-center gap-1"
            >
              <BaseLocaleLink
                v-if="newsStore.newsItem.premium"
                :to="{ name: 'news-premium' }"
                class="flex no-underline font-normal"
              >
                <BaseButton
                  size="xs"
                  look="none"
                  class="bg-pro hover:bg-pro-800 text-white"
                >
                  <BaseIcon
                    name="solid_lightning_bolt"
                    size="xs"
                    class="mr-px"
                  />
                  {{ $t('common.premium') }}
                </BaseButton>
              </BaseLocaleLink>
              <BaseLocaleLink
                v-for="item in categories"
                :key="item.id"
                :to="item.route"
                class="flex no-underline font-normal"
                :class="item.hidden && 'pointer-events-none'"
              >
                <BaseButton
                  size="xs"
                  look="none"
                  class="bg-primary-100 hover:bg-primary-400"
                >
                  {{ item.local }}
                </BaseButton>
              </BaseLocaleLink>
            </div>
            <span class="text-xs sm:text-sm whitespace-nowrap text-gray-400">
              {{ newsStore.formatDate(newsStore.newsItem.publish_date) }}
            </span>
          </div>
          <BaseButton
            look="link"
            theme="dark"
            class="font-normal hidden sm:inline-flex"
            size="xs"
            @click="goBackToNews"
          >
            <BaseIcon name="outline_arrow_turn_left" size="xs" class="mr-2" />
            <span class="text-sm">
              {{ $t('news.back_to_news') }}
            </span>
          </BaseButton>
        </div>
        <div class="relative prose news-item max-w-none dark:prose-dark">
          <h1 class="text-2xl lg:text-4xl font-bold my-4">
            {{ newsStore.newsItem.name }}
          </h1>
          <div v-html="newsDetail" />
          <div
            v-if="newsStore.newsItem.premium && authStore.isProActive"
            id="premium"
            style="scroll-margin-top: calc(8rem + var(--sat))"
            v-html="newsPremiumDetail"
          />
          <div
            v-if="newsStore.newsItem.premium && !authStore.isProActive"
            class="absolute w-full bottom-0 h-16 pointer-events-none"
            style="
              background: linear-gradient(
                0deg,
                rgba(255, 255, 255, 1) 3%,
                rgba(255, 255, 255, 0.85) 25%,
                rgba(255, 255, 255, 0.4) 100%
              );
            "
          />
        </div>
        <BaseLink
          v-if="newsStore.newsItem.source && !newsStore.newsItem.premium"
          target="_blank"
          theme="light"
          :href="newsStore.newsItem.source"
          class="text-sm no-underline !font-normal"
          proxy-class="flex items-center"
        >
          <BaseIcon name="outline_globe_alt" size="xs" class="mr-2 mt-[2px]" />
          {{ extractHost(newsStore.newsItem.source) }}
        </BaseLink>
        <div
          v-if="newsStore.newsItem.premium && !authStore.isProActive"
          id="premium"
          class="bg-pro-100 !mt-6 border border-primary rounded-xl px-6 py-5 flex flex-col sm:flex-row items-center text-center sm:text-left gap-5"
          style="scroll-margin-top: calc(7rem + var(--sat))"
        >
          <img
            src="/images/premium-logo.png"
            class="w-[66px] h-[77px] sm:w-[119px] sm:h-[159px]"
          />
          <div class="text-gray-900 space-y-3">
            <div class="text-2xl font-bold">
              {{ $t('news.unlock_title') }}
            </div>
            <div class="text-base">
              {{ $t('news.unlock_description') }}
            </div>
            <BaseLink :to="{ name: 'media-pro' }">
              <BaseButton>{{ $t('common.subscribe') }}</BaseButton>
            </BaseLink>
          </div>
        </div>
        <CardActionsPanel
          class="!mt-8 pt-4 border-t border-gray-400"
          :name="newsStore.newsItem.name"
          :comments="commentsCounter"
          :reactions="reactionsCounters"
          :page-link="pageLink"
          :is-counters-processing="isCountersProcessing"
          @change-reaction="onChangeReaction"
        >
          <FavoritesButton
            v-if="authStore.auth"
            :entity-title="newsStore.getShortName(newsStore.newsItem.name)"
            :entity-id="newsStore.newsItem.id"
            :entity-type="EntityType.Post"
          />
        </CardActionsPanel>
      </article>
      <div
        ref="commentsElement"
        class="p-4 sm:p-8 bg-white rounded-lg"
        style="scroll-margin-top: calc(4rem + var(--sat))"
      >
        <CommentsList
          :id="id"
          :total="commentsCounter"
          :entity-type="EntityType.Post"
          @loaded="scrollToComments"
          @create="onCommentCreate"
        />
      </div>
      <div>
        <div class="text-2xl text-gray-900 font-bold my-4">
          {{ $t('news.related_news') }}
        </div>
        <NewsList
          :list="
            newsStore.list.hits.filter(
              (item) => item.id !== newsStore.newsItem?.id
            )
          "
          :loaded="newsStore.list.loaded"
        />
        <BaseObserve
          v-if="newsStore.list.loaded && !newsStore.allRecordsLoaded"
          @change="nextPage"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import type { RouteLocationRaw } from 'vue-router'
import { useNews } from '~/stores/news'
// import { extractHost } from '~/utils/extract-host'
import { EntityType } from '~/models/common'
import { useNavigation } from '~/stores/navigation'
import { useAuth } from '~/stores/auth'
import { NewsEndpointMap } from '~/models/news'
import { NavigationSortType } from '~/models/navigation'

const nuxtApp = useNuxtApp()
const newsStore = useNews()
const route = useRoute()
const router = useRouter()
const { getCategoryItemsByIds, getPageLinkCategorySlug } = useNavigation()
const authStore = useAuth()
const i18n = useI18n()

const commentsElement = ref<HTMLElement | null>(null)

const id = Array.isArray(route.params.id)
  ? Number(route.params.id[0])
  : Number(route.params.id)

const categories = computed(() =>
  getCategoryItemsByIds(newsStore.newsItem?.categories || [], 'news')
)

const categorySlug = computed(() => getPageLinkCategorySlug(categories.value))
const pageLink = computed(() => {
  return newsStore.getNewsPageLink(id, categorySlug.value)
})

const entity = {
  entity_id: id,
  entity_type: EntityType.Post,
}

const newsEndpoint = computed(() =>
  authStore.auth ? NewsEndpointMap.Auth : NewsEndpointMap.Base
)

const newsDetail = computed(() =>
  newsStore.clearNewsDetail(newsStore.newsItem?.detail || '')
)

const newsPremiumDetail = computed(() =>
  newsStore.clearNewsDetail(newsStore.newsItem?.premium_detail || '')
)

const {
  initCountersHelper,
  commentsCounter,
  reactionsCounters,
  updateCommentsCounter,
  onChangeReaction,
  isCountersProcessing,
} = useCountersHelper()

onServerPrefetch(async () => {
  await fetchNewsItem()
})

onMounted(async () => {
  await fetchNewsItem()
  await initCountersHelper({
    entity,
    isLoadComments: true,
    isLoadReactions: true,
  })
  getRelatedNews()
})

watch(
  () => !!authStore.auth,
  () => {
    fetchNewsItem()
  }
)

watch(i18n.locale, async () => {
  await fetchNewsItem()
})

function scrollToComments(): void {
  route.query.scroll === 'comments' &&
    commentsElement.value?.scrollIntoView({ behavior: 'smooth' })
}

function onCommentCreate(): void {
  updateCommentsCounter(commentsCounter.value + 1)
}

async function fetchNewsItem(): Promise<void> {
  await newsStore.fetchNewsItem(id, newsEndpoint.value)
  nuxtApp.runWithContext(() => initMeta())
}

function initMeta(): void {
  if (newsStore.newsItem) {
    const tagsNames = newsStore.newsItem.tags.map((item) => item.name)
    const categoriesNames = categories.value.map((item) => item.local)

    useHeadMeta(
      {
        title: newsStore.newsItem.name.trim(),
        description: newsStore.newsItem.preview?.trim(),
        keywords: tagsNames.join(', '),
        image: newsStore.newsItem.preview_img,
        article: {
          publishedTime: newsStore.newsItem.publish_date,
          sections: categoriesNames,
          tags: tagsNames,
        },
      },
      { canonical: pageLink }
    )
  }
}

function goBackToNews(): void {
  const prevRoute = router.options.history.state.back?.toString() || ''

  // TODO: return when we return news page
  // if (prevRoute.includes('news') && !prevRoute.includes('item-')) {
  //   router.back()
  // } else {
  //   router.push(useLocaleRoute()({ name: 'news' }) as RouteLocationRaw)
  // }
  if (!prevRoute.includes('item-')) {
    router.back()
  } else {
    router.push(useLocaleRoute()({ name: 'index' }) as RouteLocationRaw)
  }
}

async function getRelatedNews(): Promise<void> {
  newsStore.resetNewsListWithSorting(
    newsEndpoint.value,
    NavigationSortType.All,
    categories.value[0].id as number
  )
  await newsStore.getNews()
}

async function nextPage(visible: boolean): Promise<void> {
  if (visible) {
    await newsStore.nextPage()
  }
}
</script>

<style lang="scss">
$md: 768px;
$openQuote: '\201C';
$closeQuote: '\201D';
$textColor: #111827;
$textColorDark: #f5f5f5;
$borderColor: #e5e7eb;
$borderColorDark: #525252;
$primaryColor: #0d65d8;

.news-item blockquote {
  quotes: none;

  p:first-child {
    &::before {
      content: $openQuote;
    }

    &::after {
      content: $closeQuote;
    }
  }

  p:last-child {
    font-style: normal;
  }
}

.news-item img {
  border-radius: 0.5rem;
  width: auto;
  max-width: 100%;
  height: auto;
  margin-left: auto;
  margin-right: auto;
}

.news-item.prose .quote {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  justify-content: center;
  font-weight: 500;
  color: $textColor;
  border-left-width: 0.25rem;
  border-left-color: $borderColor;
  quotes: none;
  margin: 1.6em 0;
  padding-left: 1.6em;

  .phrase {
    font-style: italic;
    margin-top: 0;

    &::before {
      content: $openQuote;
    }

    &::after {
      content: $closeQuote;
    }
  }

  .author {
    font-size: 1.4em;
    margin-top: 0;
    margin-bottom: 0;
  }

  .job {
    margin: 0;
  }

  .author-photo {
    flex-shrink: 0;
    max-width: 10em;
    margin-bottom: 1em;

    img {
      border-radius: 100%;
      margin: 0;
    }

    @media (min-width: $md) {
      margin-right: 1.6em;
      margin-bottom: 0;
    }
  }

  @media (min-width: $md) {
    flex-wrap: nowrap;
  }
}

.news-item .article-source {
  display: flex;
  align-items: center;
  line-height: 1;
  margin-top: 1.25em;
  margin-bottom: 1.25em;

  .article-source-title {
    margin: 0;
  }

  .article-source-link {
    display: flex;
    margin-left: 0.375rem;
    align-items: center;
    text-decoration: none;
    font-size: 1.25rem;
    font-weight: 100;

    .article-source-icon {
      width: 1rem;
      height: 1rem;
      margin: 0 0.375rem;
      border-radius: 0;
    }

    &:hover {
      opacity: 0.8;
    }
  }
}

.news-item .article-attachments-items {
  display: flex;
  flex-wrap: wrap;
  gap: 1.25em;
  margin-top: 1.25em;
  margin-bottom: 1.25em;

  .article-attachment {
    display: flex;
    align-items: center;
    color: inherit;
    border-radius: 0.75rem;
    background-color: #e4effd;
    text-decoration: none;

    .article-attachment-icon {
      background-color: $primaryColor;
      border-radius: 0.75rem;
      padding: 0.375rem 0.875rem;
      color: white;
      font-size: 0.75rem;
      text-align: center;

      svg {
        width: 1.5rem;
        height: 1.5rem;
      }

      .article-attachment-extension {
        font-size: 0.75rem;
        line-height: 1rem;
      }
    }

    .article-attachment-info {
      display: flex;
      flex-direction: column;
      padding: 0.375rem 0.875rem;

      .article-attachment-title {
        font-size: 0.875rem;
        line-height: 1.25rem;
      }

      .article-attachment-filesize {
        color: #75849a;
        font-size: 0.75rem;
        line-height: 1rem;
      }
    }

    &:hover {
      background-color: #95c0f9;
    }
  }
}

.dark .news-item.dark\:prose-dark .quote {
  color: $textColorDark;
  border-left-color: $borderColorDark;
}
</style>
